import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from '@tiptap/markdown';
import { EditorContent,useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback,useEffect, useState } from 'react';

import { EditorControls } from './components/EditorControls/EditorControls';
import { EditorToolbar } from './components/EditorToolbar/EditorToolbar';
import classes from './EditorRedact.module.scss';

interface EditorRedactProps {
	initialValue?: string;
	withActions?: boolean;
	placeholder?: string;
	onChange?: (value: string) => void;
	onSave?: () => void;
	onCancel?: () => void;
}

export const EditorRedact = ({
	initialValue = '',
	withActions = false,
	placeholder = '',
	onChange,
	onSave,
	onCancel,
}: EditorRedactProps) => {
	const [value, setValue] = useState<string>(initialValue);
	const [isDisabled, setIsDisabled] = useState<boolean>(true);
	const [currentHeading, setCurrentHeading] = useState<string>('Текст');
	const [showLinkPopup, setShowLinkPopup] = useState<boolean>(false);
	const [showImagePopup, setShowImagePopup] = useState<boolean>(false);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: {
					levels: [1, 2, 3, 4, 5, 6],
				},
				bulletList: {
					keepMarks: true,
					keepAttributes: false,
				},
				orderedList: {
					keepMarks: true,
					keepAttributes: false,
				},
			}),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					target: '_blank',
				},
			}),
			Image.configure({
				inline: true,
				allowBase64: true,
			}),
			Placeholder.configure({
				placeholder,
			}),
			Markdown,
		],
		content: value,
		contentType: 'markdown',
		editable: true,
		editorProps: {
			attributes: {
				class: classes.editorContent,
				spellcheck: 'false',
			},
		},
		onUpdate: ({ editor }) => {
			const newValue = editor.getMarkdown() || editor.getHTML();
			setValue(newValue);
			onChange?.(newValue);

			if (editor.isActive('heading', { level: 1 })) {
				setCurrentHeading('Заголовок 1');
			} else if (editor.isActive('heading', { level: 2 })) {
				setCurrentHeading('Заголовок 2');
			} else if (editor.isActive('heading', { level: 3 })) {
				setCurrentHeading('Заголовок 3');
			} else if (editor.isActive('heading', { level: 4 })) {
				setCurrentHeading('Заголовок 4');
			} else if (editor.isActive('heading', { level: 5 })) {
				setCurrentHeading('Заголовок 5');
			} else if (editor.isActive('heading', { level: 6 })) {
				setCurrentHeading('Заголовок 6');
			} else {
				setCurrentHeading('Текст');
			}
		},
	});

	useEffect(() => {
		if (editor && initialValue !== value) {
			editor.commands.setContent(initialValue);
			setValue(initialValue);
			const hasContent = initialValue.trim().length > 0;
			setIsDisabled(!hasContent);
		}
	}, [initialValue, editor]);

	useEffect(() => {
		const hasContent = value.trim().length > 0;
		setIsDisabled(!hasContent);
	}, [value]);

	const handleBold = useCallback(() => {
		editor?.chain().focus().toggleBold().run();
	}, [editor]);

	const handleItalic = useCallback(() => {
		editor?.chain().focus().toggleItalic().run();
	}, [editor]);

	const handleStrike = useCallback(() => {
		editor?.chain().focus().toggleStrike().run();
	}, [editor]);

	const handleHeading = useCallback(
		(level: 1 | 2 | 3 | 4 | 5 | 6) => {
			editor?.chain().focus().toggleHeading({ level }).run();
		},
		[editor]
	);

	const handleParagraph = useCallback(() => {
		editor?.chain().focus().setParagraph().run();
		setCurrentHeading('Текст');
	}, [editor]);

	const handleBulletList = useCallback(() => {
		editor?.chain().focus().toggleBulletList().run();
	}, [editor]);

	const handleOrderedList = useCallback(() => {
		editor?.chain().focus().toggleOrderedList().run();
	}, [editor]);

	const handleBlockquote = useCallback(() => {
		editor?.chain().focus().toggleBlockquote().run();
	}, [editor]);

	return (
		<div className={classes.editor}>
			<EditorToolbar
				editor={editor}
				currentHeading={currentHeading}
				showLinkPopup={showLinkPopup}
				showImagePopup={showImagePopup}
				onBold={handleBold}
				onItalic={handleItalic}
				onStrike={handleStrike}
				onHeading={handleHeading}
				onParagraph={handleParagraph}
				onBulletList={handleBulletList}
				onOrderedList={handleOrderedList}
				onBlockquote={handleBlockquote}
				onLinkPopupToggle={setShowLinkPopup}
				onImagePopupToggle={setShowImagePopup}
			/>
			<div className={classes.editorArea}>
				<EditorContent editor={editor} />
				{withActions && (
					<EditorControls isDisabled={isDisabled} onSave={onSave} onCancel={onCancel} />
				)}
			</div>
		</div>
	);
};

export default EditorRedact;
